import { COAGroup } from '@futrli-sdk/predict';

import { COAFullStructureWithDisplayCodesFlag } from '../predict/src/core/api/types/chart-of-accounts';

import { ApiView, ApiViewState, getDefaultApiViewState } from './core/ApiView';
import { chartOfAccountsQuery } from './queries/chartOfAccounts';
import {
  updateGroupMutation,
  deleteGroupMutation,
  updateCOAStructureMutation,
  recategoriseAccountMutation,
  setSwitchRuleMutation,
  deleteSwitchRuleMutation,
  UpdateGroupInput,
  DeleteGroupInput,
  UpdateCOAStructureInput,
  RecategoriseAccountInput,
  SetSwitchRuleInput,
  DeleteSwitchRuleInput,
  createGroupAndUpdateStructureMutation,
  CreateGroupAndUpdateStructureInput
} from './mutations/chartOfAccounts';
import { COA_TYPE, COADisplayAccount } from '../predict/src/chart-of-accounts/types';
import { getCOAFullStructure, getDisplayAccountsTree } from '@futrli/predict/chart-of-accounts/utils';

export interface MutateCallbacks<TData> {
  onSuccess?: (data: TData) => void;
  onError?: (error: Error) => void;
}

export interface ChartOfAccountsViewState extends ApiViewState {
  errorDeletingGroup: Error | null;
  errorDeletingSwitchRule: Error | null;
  errorRecategorising: Error | null;
  errorSettingSwitchRule: Error | null;
  errorUpdatingGroup: Error | null;
  errorUpdatingStructure: Error | null;
  errorCreatingGroupAndStructure: Error | null;
  isCreatingGroupAndStructure: boolean;
  isDeletingGroup: boolean;
  isDeletingSwitchRule: boolean;
  isRecategorising: boolean;
  isSettingSwitchRule: boolean;
  isUpdatingGroup: boolean;
  isUpdatingStructure: boolean;
  structure: COAFullStructureWithDisplayCodesFlag | null;
  profitAndLossTree: COADisplayAccount | null;
  balanceSheetTree: COADisplayAccount | null;
}

export class ChartOfAccountsView extends ApiView {
  private structureObs: ReturnType<typeof chartOfAccountsQuery>;

  private createGroupAndStructureMut: ReturnType<typeof createGroupAndUpdateStructureMutation>;

  private updateGroupMut: ReturnType<typeof updateGroupMutation>;

  private deleteGroupMut: ReturnType<typeof deleteGroupMutation>;

  private updateStructureMut: ReturnType<typeof updateCOAStructureMutation>;

  private recategoriseMut: ReturnType<typeof recategoriseAccountMutation>;

  private setSwitchRuleMut: ReturnType<typeof setSwitchRuleMutation>;

  private deleteSwitchRuleMut: ReturnType<typeof deleteSwitchRuleMutation>;

  private cachedRawStructure: COAFullStructureWithDisplayCodesFlag | null = null;
  private cachedPnLTree: COADisplayAccount | null = null;
  private cachedBSTree: COADisplayAccount | null = null;

  public state: ChartOfAccountsViewState = {
    ...getDefaultApiViewState(),
    errorDeletingGroup: null,
    errorDeletingSwitchRule: null,
    errorRecategorising: null,
    errorSettingSwitchRule: null,
    errorUpdatingGroup: null,
    errorUpdatingStructure: null,
    isDeletingGroup: false,
    isDeletingSwitchRule: false,
    isRecategorising: false,
    isSettingSwitchRule: false,
    isUpdatingGroup: false,
    isUpdatingStructure: false,
    structure: null,
    errorCreatingGroupAndStructure: null,
    isCreatingGroupAndStructure: false,
    profitAndLossTree: null,
    balanceSheetTree: null
  };

  constructor(private workspaceId: string, private organisationId: string) {
    super();

    // Query
    this.structureObs = this.addQuery(chartOfAccountsQuery(workspaceId, organisationId));

    // Mutations
    this.updateGroupMut = this.addMutation(updateGroupMutation(workspaceId, organisationId));
    this.deleteGroupMut = this.addMutation(deleteGroupMutation(workspaceId, organisationId));
    this.updateStructureMut = this.addMutation(updateCOAStructureMutation(workspaceId, organisationId));
    this.recategoriseMut = this.addMutation(recategoriseAccountMutation(workspaceId, organisationId));
    this.setSwitchRuleMut = this.addMutation(setSwitchRuleMutation(workspaceId, organisationId));
    this.deleteSwitchRuleMut = this.addMutation(deleteSwitchRuleMutation(workspaceId, organisationId));
    this.createGroupAndStructureMut = this.addMutation(
      createGroupAndUpdateStructureMutation(workspaceId, organisationId)
    );
    this.updateState();
  }

  // ***************
  // *  Public API *
  // ***************
  public createGroupAndUpdateStructure(
    data: CreateGroupAndUpdateStructureInput,
    callbacks?: MutateCallbacks<COAGroup>
  ) {
    return this.createGroupAndStructureMut.mutate(data, callbacks);
  }

  public saveReorderedTree(draftTree: COADisplayAccount, coaType: COA_TYPE, callbacks?: MutateCallbacks<void>) {
    const isPnL = coaType === COA_TYPE.PROFIT_AND_LOSS;

    const payload: UpdateCOAStructureInput = {
      profit_and_loss: isPnL
        ? getCOAFullStructure(draftTree.children)
        : getCOAFullStructure(this.state.profitAndLossTree?.children || []),
      balance_sheet: !isPnL
        ? getCOAFullStructure(draftTree.children)
        : getCOAFullStructure(this.state.balanceSheetTree?.children || [])
    };

    return this.updateStructure(payload, callbacks);
  }

  public deleteGroup(groupId: DeleteGroupInput, callbacks?: MutateCallbacks<void>) {
    return this.deleteGroupMut.mutate(groupId, callbacks);
  }

  public deleteSwitchRule(accountId: DeleteSwitchRuleInput, callbacks?: MutateCallbacks<void>) {
    return this.deleteSwitchRuleMut.mutate(accountId, callbacks);
  }

  public recategoriseAccount(data: RecategoriseAccountInput, callbacks?: MutateCallbacks<void>) {
    return this.recategoriseMut.mutate(data, callbacks);
  }

  public setSwitchRule(data: SetSwitchRuleInput, callbacks?: MutateCallbacks<void>) {
    return this.setSwitchRuleMut.mutate(data, callbacks);
  }

  public updateGroup(data: UpdateGroupInput, callbacks?: MutateCallbacks<COAGroup>) {
    return this.updateGroupMut.mutate(data, callbacks);
  }

  public updateStructure(data: UpdateCOAStructureInput, callbacks?: MutateCallbacks<void>) {
    return this.updateStructureMut.mutate(data, callbacks);
  }

  // ****************************
  // *  Private / Protected API *
  // ****************************
  private get structure(): COAFullStructureWithDisplayCodesFlag | null {
    return this.getResult(this.structureObs).data ?? null;
  }

  private computeProfitAndLossTree(current: COAFullStructureWithDisplayCodesFlag | null): COADisplayAccount | null {
    if (!current) {
      return null;
    }
    if (current === this.cachedRawStructure && this.cachedPnLTree) {
      return this.cachedPnLTree;
    }
    return getDisplayAccountsTree(current.profit_and_loss);
  }

  private computeBalanceSheetTree(current: COAFullStructureWithDisplayCodesFlag | null): COADisplayAccount | null {
    if (!current) {
      return null;
    }
    if (current === this.cachedRawStructure && this.cachedBSTree) {
      return this.cachedBSTree;
    }
    return getDisplayAccountsTree(current.balance_sheet);
  }
  protected getNewState(): ChartOfAccountsViewState {
    // Compute the trees using the cache helpers
    const currentStructure = this.structure;
    const pnlTree = this.computeProfitAndLossTree(currentStructure);
    const bsTree = this.computeBalanceSheetTree(currentStructure);

    // Update the cache reference tracking AFTER computing both
    this.cachedRawStructure = currentStructure;
    this.cachedPnLTree = pnlTree;
    this.cachedBSTree = bsTree;
    return {
      ...super.getNewState(),
      errorDeletingGroup: this.deleteGroupMut.getCurrentResult().error,
      errorDeletingSwitchRule: this.deleteSwitchRuleMut.getCurrentResult().error,
      errorRecategorising: this.recategoriseMut.getCurrentResult().error,
      errorSettingSwitchRule: this.setSwitchRuleMut.getCurrentResult().error,
      errorUpdatingGroup: this.updateGroupMut.getCurrentResult().error,
      errorUpdatingStructure: this.updateStructureMut.getCurrentResult().error,
      isDeletingGroup: this.deleteGroupMut.getCurrentResult().status === 'pending',
      isDeletingSwitchRule: this.deleteSwitchRuleMut.getCurrentResult().status === 'pending',
      isRecategorising: this.recategoriseMut.getCurrentResult().status === 'pending',
      isSettingSwitchRule: this.setSwitchRuleMut.getCurrentResult().status === 'pending',
      isUpdatingGroup: this.updateGroupMut.getCurrentResult().status === 'pending',
      isUpdatingStructure: this.updateStructureMut.getCurrentResult().status === 'pending',
      structure: this.structure,
      errorCreatingGroupAndStructure: this.createGroupAndStructureMut.getCurrentResult().error,
      isCreatingGroupAndStructure: this.createGroupAndStructureMut.getCurrentResult().status === 'pending',
      profitAndLossTree: pnlTree,
      balanceSheetTree: bsTree
    };
  }
}
